import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import { useTokenColors } from '../../utils/themeColor';
import './StatsCard.scss';

/**
 * StatsCard — the single KPI card used across the app.
 *
 * This is the dashboard KPI card (icon tile + uppercase label + big value +
 * optional trend / sparkline), promoted out of Dashboard/StatCard.js so every
 * page renders the same thing. Dashboard/StatCard.js now delegates here.
 *
 * Props are deliberately forgiving because call sites grew apart before this
 * was centralised: `label` is accepted for `title`, and `color` / `iconClass`
 * for `iconColor`, including the older green/purple/orange/red/yellow names.
 * `icon` takes either a component (`icon={Users}`) or an element
 * (`icon={<Users size={20} />}`).
 */

/**
 * The sparkline's ink, per tone.
 *
 * ⚠️ These are the SAME six tones StatsCard.scss paints the icon tile with, and
 * until 2026-08-25 the two disagreed: the tile read a token while the sparkline
 * four pixels away painted a hardcoded hex, so in dark mode one card carried two
 * violets. Keep this map and StatsCard.scss's tone block in step — they colour the
 * same card. `blue` and `cyan` are both --info: they are legacy tone names, not two
 * hues. A tone here is a STATUS family, not a --chart-N: a KPI tile is not a chart
 * series, and only `violet` has no status twin.
 *
 * The hexes could not be found by any gate in this repo: `--check literal` reads
 * stylesheets and `--check inline` reads style={{}}, and a JS map is neither.
 * Same blind spot as reactSelectTheme.js — see the Phase 5 fifth-batch note.
 *
 * `primary` is the institute's brand and is the only tone here that moves at
 * runtime — applyBrandTokens() rewrites --primary per institute. It used to be an
 * ALIAS of `blue`, so the one tone name that promises to follow the customer's
 * brand was the only one that could not, which is the same defect AppBadge's
 * `color="primary"` carried until it was tokenised.
 */
const SPARK_TOKENS = {
  primary: '--primary',
  blue: '--info',
  violet: '--chart-1',
  emerald: '--success',
  rose: '--danger',
  amber: '--warning',
  cyan: '--info',
};

// Legacy colour names used by older call sites → the six canonical tones.
const COLOR_ALIASES = {
  green: 'emerald',
  purple: 'violet',
  orange: 'amber',
  yellow: 'amber',
  red: 'rose',
  danger: 'rose',
  warning: 'amber',
  success: 'emerald',
  info: 'cyan',
  teal: 'cyan',
  indigo: 'violet',
};

const normalizeColor = (c) => {
  if (!c) return 'blue';
  const key = String(c).trim().toLowerCase();
  return COLOR_ALIASES[key] || (SPARK_TOKENS[key] ? key : 'blue');
};

const StatsCard = ({
  title,
  label,            // alias for title
  value,
  suffix,
  subtitle,
  icon: Icon,
  iconColor,
  color,            // alias for iconColor
  iconClass,        // alias for iconColor (Dashboard/StatCard legacy)
  gradient,         // legacy tint prop — now only a colour hint
  change,
  changeType = 'positive',
  changeLabel = 'vs last period',
  series,
  onClick,
  loading = false,
  className = '',
}) => {
  const heading = title ?? label;
  const tone = normalizeColor(iconColor || color || iconClass || gradient);
  // Called unconditionally, above the `loading` early return -- a hook cannot
  // sit behind it. recharts takes fill/stroke as SVG presentation attributes,
  // where var() does not resolve, so the token has to be read as a value.
  const sparkInk = useTokenColors(SPARK_TOKENS);
  const isPositive = changeType === 'positive';
  const clickable = typeof onClick === 'function';

  const sparkData =
    Array.isArray(series) && series.length > 1
      ? series.map((v, i) => ({ i, v: Number(v) || 0 }))
      : null;
  const sparkColor = sparkInk[tone];
  const sparkId = `stats-spark-${tone}`;

  // `icon` may be a component type or an already-built element.
  const iconNode = !Icon
    ? null
    : React.isValidElement(Icon)
      ? Icon
      : <Icon size={20} />;

  if (loading) {
    return (
      <div className={`stats-card stats-card--loading ${className}`} aria-busy="true">
        <div className="stats-card__inner">
          <div className="stats-card__skeleton stats-card__skeleton--icon" />
          <div className="stats-card__info">
            <div className="stats-card__skeleton stats-card__skeleton--label" />
            <div className="stats-card__skeleton stats-card__skeleton--value" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`stats-card ${clickable ? 'is-clickable' : ''} ${className}`}
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(e);
        }
      } : undefined}
    >
      <div className="stats-card__inner">
        {iconNode && <div className={`stats-card__icon ${tone}`}>{iconNode}</div>}

        <div className="stats-card__info">
          <span className="stats-card__label">{heading}</span>
          <div className="stats-card__value">
            {value}
            {suffix && <span className="stats-card__suffix">{suffix}</span>}
          </div>

          {change && (
            <div className={`stats-card__trend ${isPositive ? 'positive' : 'negative'}`}>
              {isPositive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              <span className="stats-card__trend-value">{change}</span>
              {changeLabel && <div className="stats-card__trend-label">{changeLabel}</div>}
            </div>
          )}

          {!change && subtitle && (
            <div className="stats-card__subtitle">{subtitle}</div>
          )}
        </div>

        {sparkData && (
          <div className="stats-card__spark">
            <ResponsiveContainer width="100%" height={36}>
              <AreaChart data={sparkData} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id={sparkId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={sparkColor} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={sparkColor} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="v"
                  stroke={sparkColor}
                  strokeWidth={1.75}
                  fill={`url(#${sparkId})`}
                  dot={false}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatsCard;
